/**
 * progressStore.ts
 *
 * localStorage-based performance log. Stores raw PerformanceRecords and
 * derives dashboard stats on demand. Write through `practiceTracking.ts`.
 */

import { PerformanceRecord, GlobalStats } from '../types/progress';

const STORAGE_KEY = 'jamcam-progress-v1';
const MAX_RECORDS = 2000; // cap to prevent unbounded growth

/* ------------------------------------------------------------------ */
/*  Low-level storage                                                  */
/* ------------------------------------------------------------------ */

function loadRecords(): PerformanceRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecords(records: PerformanceRecord[]): void {
  const trimmed =
    records.length > MAX_RECORDS
      ? records.slice(records.length - MAX_RECORDS)
      : records;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // Storage can be blocked or full; stats are best-effort.
  }
}

/* ------------------------------------------------------------------ */
/*  Public API                                                         */
/* ------------------------------------------------------------------ */

export function recordAttempt(record: PerformanceRecord): void {
  const records = loadRecords();
  records.push(record);
  saveRecords(records);
}

/* ------------------------------------------------------------------ */
/*  Derived summaries                                                  */
/* ------------------------------------------------------------------ */

function toTimestamp(iso: string): number {
  return new Date(iso).getTime();
}

export function getGlobalStats(): GlobalStats {
  const records = loadRecords();
  if (records.length === 0) {
    return {
      totalPracticeTime: 0,
      totalSessionsCompleted: 0,
      dailyStreak: 0,
      longestDailyStreak: 0,
      firstSessionDate: '',
      lastSessionDate: '',
    };
  }

  const timestamps = records.map((r) => toTimestamp(r.startedAt));
  const totalPracticeTime = records.reduce(
    (sum, r) => sum + r.durationSeconds,
    0
  );
  const completed = records.filter((r) => r.completed);

  const dates = records
    .map((r) => r.startedAt)
    .filter(Boolean)
    .sort();

  return {
    totalPracticeTime,
    totalSessionsCompleted: completed.length,
    dailyStreak: computeStreak(timestamps),
    longestDailyStreak: computeLongestStreak(timestamps),
    firstSessionDate: dates[0] ?? '',
    lastSessionDate: dates[dates.length - 1] ?? '',
  };
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function computeStreak(timestamps: number[]): number {
  if (timestamps.length === 0) return 0;

  const dayMs = 86_400_000;
  const uniqueDays = new Set(timestamps.map((t) => Math.floor(t / dayMs)));
  const sorted = Array.from(uniqueDays).sort((a, b) => b - a);

  const today = Math.floor(Date.now() / dayMs);
  if (sorted[0] < today - 1) return 0; // no practice today or yesterday

  let streak = 0;
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i] === today - i || sorted[i] === today - i + 1) {
      streak++;
    } else {
      break;
    }
  }
  return streak;
}

function computeLongestStreak(timestamps: number[]): number {
  if (timestamps.length === 0) return 0;

  const dayMs = 86_400_000;
  const uniqueDays = Array.from(
    new Set(timestamps.map((t) => Math.floor(t / dayMs)))
  ).sort((a, b) => a - b);

  let longest = 1;
  let current = 1;
  for (let i = 1; i < uniqueDays.length; i++) {
    if (uniqueDays[i] === uniqueDays[i - 1] + 1) {
      current++;
      longest = Math.max(longest, current);
    } else {
      current = 1;
    }
  }
  return longest;
}
